import { c as _c } from "react/compiler-runtime";
// Все настройки уведомлений покупателя — раздел кабинета «Уведомления →
// Настройки» (/profile/notifications/settings). Вписано в визуальный язык
// макета «Профиль»: серая панель ProfilePanel, белые карточки r20, чекбоксы
// #FF5E00, чипы h32 с оранжевым контуром у выбранного, кнопки h40 r10.
//
// Что здесь (доделка ТЗ 31.10.2026):
//  - категории уведомлений (fd-notif-02);
//  - частота маркетинговых: сразу / раз в день / раз в неделю / не присылать —
//    шлюз копит и шлёт сводкой (fd-notif-14);
//  - «Похожие подборки недели» и сигналы о цене и наличии (fd-notif-01/06);
//  - отписки от магазинов и от категорий товаров (fd-notif-15);
//  - Telegram: привязка по ссылке на бота и отвязка (fd-platform-04); без бота
//    на сервере блока нет.
// Каждое изменение сохраняется сразу, как у тумблеров в окне «Настройка уведомлений».

import { Skeleton } from "@osmix/ui/skeleton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { api, type Category } from "@/api";
import type { NotificationCategoryToggles, NotificationMarketingFrequency, NotificationPreferencesUpdate } from "@/api/types";
import { notificationKeys, useNotificationPreferences, useUpdateNotificationPreferences } from "@/api/use-notifications";
import { ErrorState } from "@/components/common/states";
import { ProfilePanel } from "@/components/profile/profile-shell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useProfile } from "@/store/profile";
const CATEGORY_KEYS: (keyof NotificationCategoryToggles)[] = ["trade", "social", "likes", "marketing", "financial", "analytics", "system"];
const FREQUENCIES: NotificationMarketingFrequency[] = ["immediate", "daily", "weekly", "off"];

/** Дерево категорий → плоский список «Женщинам / Платья» для выбора отписки. */
const flattenCategories = (nodes: Category[], trail: string[] = []): Array<{
  id: string;
  label: string;
}> => nodes.flatMap(node => [{
  id: node.id,
  label: [...trail, node.name].join(" / ")
}, ...flattenCategories(node.children ?? [], [...trail, node.name])]);
function Card(t0) {
  const $ = _c(10);
  const {
    title,
    hint,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h2 className="text-[16px] leading-[21px] font-medium text-[#1F1F1F]">{title}</h2>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hint) {
    t2 = hint ? <p className="mt-0.5 text-[12px] leading-4 text-black/50">{hint}</p> : null;
    $[2] = hint;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children) {
    t3 = <div className="mt-3">{children}</div>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <section className="rounded-[20px] bg-white p-4">{t1}{t2}{t3}</section>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function CheckRow(t0) {
  const $ = _c(27);
  const {
    checked,
    onChange,
    title,
    hint,
    disabled
  } = t0;
  const t1 = disabled && "cursor-default opacity-60";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex cursor-pointer items-start gap-2.5", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onChange) {
    t3 = e => onChange(e.target.checked);
    $[2] = onChange;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== checked || $[5] !== disabled || $[6] !== t3) {
    t4 = <input type="checkbox" checked={checked} disabled={disabled} onChange={t3} className="peer sr-only" />;
    $[4] = checked;
    $[5] = disabled;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = checked ? "border-[#FF5E00] bg-[#FF5E00] text-white" : "border-[#1F1F1F]/24";
  let t6;
  if ($[8] !== t5) {
    t6 = cn("mt-[1px] grid size-5 shrink-0 place-items-center rounded-[5px] border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#1F1F1F]/40", t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== checked) {
    t7 = checked && <Check className="size-3.5" strokeWidth={2.5} />;
    $[10] = checked;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <span aria-hidden="true" className={t6}>{t7}</span>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== title) {
    t9 = <span className="block text-[14px] leading-[18px] font-medium text-[#1F1F1F]">{title}</span>;
    $[15] = title;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== hint) {
    t10 = hint ? <span className="block text-[12px] leading-4 text-black/50">{hint}</span> : null;
    $[17] = hint;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <span className="min-w-0">{t9}{t10}</span>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t2 || $[24] !== t4 || $[25] !== t8) {
    t12 = <label className={t2}>{t4}{t8}{t11}</label>;
    $[22] = t11;
    $[23] = t2;
    $[24] = t4;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
function ChipButton(t0) {
  const $ = _c(8);
  const {
    active,
    onClick,
    children,
    disabled
  } = t0;
  const t1 = active ? "border-[#FA5C2E] bg-[#FA5C2E]/5" : "border-[#1F1F1F]/4 bg-[#1F1F1F]/3 hover:bg-[#1F1F1F]/6";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("h-8 rounded-full border-[0.5px] px-3 text-[14px] leading-[18px] font-medium text-[#1F1F1F] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40 disabled:opacity-60", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== active || $[3] !== children || $[4] !== disabled || $[5] !== onClick || $[6] !== t2) {
    t3 = <button type="button" role="radio" aria-checked={active} disabled={disabled} onClick={onClick} className={t2}>{children}</button>;
    $[2] = active;
    $[3] = children;
    $[4] = disabled;
    $[5] = onClick;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function MutedList(t0) {
  const $ = _c(13);
  const {
    items,
    empty,
    onRemove,
    removeLabel,
    disabled
  } = t0;
  if (items.length === 0) {
    let t1;
    if ($[0] !== empty) {
      t1 = <p className="rounded-[10px] bg-[#F8F8F8] px-3 py-2.5 text-[14px] leading-[18px] text-[#1F1F1F]/64">{empty}</p>;
      $[0] = empty;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== disabled || $[3] !== items || $[4] !== onRemove || $[5] !== removeLabel) {
    let t2;
    if ($[7] !== disabled || $[8] !== onRemove || $[9] !== removeLabel) {
      t2 = item => <li key={item.id} className="flex items-center justify-between gap-3 rounded-[10px] bg-[#F8F8F8] px-3 py-2"><span className="min-w-0 truncate text-[14px] leading-[18px] text-[#1F1F1F]">{item.label}</span><button type="button" disabled={disabled} onClick={() => onRemove(item.id)} className="h-8 shrink-0 rounded-[10px] bg-[#989AA2]/12 px-3 text-[13px] leading-4 font-semibold text-[#1F1F1F] outline-none hover:bg-[#989AA2]/20 focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40 disabled:opacity-60">{removeLabel}</button></li>;
      $[7] = disabled;
      $[8] = onRemove;
      $[9] = removeLabel;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = items.map(t2);
    $[2] = disabled;
    $[3] = items;
    $[4] = onRemove;
    $[5] = removeLabel;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = <ul className="flex flex-col gap-2">{t1}</ul>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}
function TelegramCard() {
  const {
    t
  } = useTranslation();
  const principalId = useProfile(s => s.user?.id ?? "guest");
  const qc = useQueryClient();
  const key = [...notificationKeys.root(principalId), "telegram"];
  const [waiting, setWaiting] = useState(false);
  const [busy, setBusy] = useState(false);
  const status = useQuery({
    queryKey: key,
    queryFn: () => api.notifications.telegramStatus(),
    // Пока человек в боте жмёт «Старт» — опрашиваем, чтобы статус сменился сам.
    refetchInterval: waiting ? 3000 : false
  });
  const startedAt = useRef(0);
  useEffect(() => {
    if (!waiting) return;
    if (status.data?.linked || Date.now() - startedAt.current > 3 * 60_000) setWaiting(false);
  }, [waiting, status.data?.linked]);
  if (!status.data?.enabled) return null;
  const connect = async () => {
    setBusy(true);
    // Окно открываем сразу по нажатию: после await браузер его заблокирует.
    const win = window.open("", "_blank");
    try {
      const link = await api.notifications.telegramLink();
      if (win) win.location.href = link.url;else window.location.href = link.url;
      startedAt.current = Date.now();
      setWaiting(true);
    } catch (e) {
      win?.close();
      toast.error(e instanceof Error ? e.message : t("notificationSettings.telegram.error"));
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    setBusy(true);
    try {
      await api.notifications.telegramUnlink();
      await qc.invalidateQueries({
        queryKey: key
      });
      toast.success(t("notificationSettings.telegram.disconnected"));
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : t("notificationSettings.telegram.error"));
    } finally {
      setBusy(false);
    }
  };
  const linked = status.data.linked;
  return <Card title={t("notificationSettings.telegram.title")} hint={t("notificationSettings.telegram.hint")}>
      {linked ? <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[14px] leading-[18px] text-[#1F1F1F]">
            {status.data.username ? t("notificationSettings.telegram.linkedAs", {
          username: status.data.username
        }) : t("notificationSettings.telegram.linked")}
          </p>
          <button type="button" disabled={busy} onClick={() => void disconnect()} className="h-10 rounded-[10px] bg-[#989AA2]/12 px-4 text-[14px] leading-[18px] font-semibold text-[#1F1F1F] outline-none hover:bg-[#989AA2]/20 focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40 disabled:opacity-60">
            {t("notificationSettings.telegram.disconnect")}
          </button>
        </div> : <div className="flex flex-col gap-2">
          <button type="button" disabled={busy} onClick={() => void connect()} className="h-10 self-start rounded-[10px] bg-[#2E2E2E] px-4 text-[14px] leading-[18px] font-semibold text-white outline-none hover:bg-[#1F1F1F] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40 disabled:opacity-60">
            {t("notificationSettings.telegram.connect", {
          bot: status.data.botUsername
        })}
          </button>
          {waiting && <p className="text-[12px] leading-4 text-[#1F1F1F]/64">
              {t("notificationSettings.telegram.waiting")}
            </p>}
        </div>}
    </Card>;
}
export function NotificationsSettingsPage() {
  const $ = _c(22);
  const {
    t
  } = useTranslation();
  const prefs = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["shops", "list"],
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const shopsQuery = useQuery(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["categories"],
      queryFn: _temp2
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const categoriesQuery = useQuery(t1);
  let t2;
  if ($[2] !== categoriesQuery.data || $[3] !== prefs || $[4] !== shopsQuery.data || $[5] !== t || $[6] !== update) {
    const shopName = new Map((shopsQuery.data ?? []).map(_temp3));
    const categoryOptions = flattenCategories(categoriesQuery.data ?? []);
    const categoryName = new Map(categoryOptions.map(_temp4));
    let t3;
    if ($[8] !== t || $[9] !== update) {
      t3 = patch => {
        update.mutate(patch, {
          onSuccess: () => toast.success(t("notificationSettings.saved")),
          onError: () => toast.error(t("notificationSettings.saveError"))
        });
      };
      $[8] = t;
      $[9] = update;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    const save = t3;
    const data = prefs.data;
    const mutedCategoryIds = data?.mutedCategoryIds ?? [];
    const marketingOn = Boolean(data?.categories.marketing) && data?.marketingFrequency !== "off";
    let t4;
    if ($[11] !== t) {
      t4 = t("notificationSettings.crumb");
      $[11] = t;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t4) {
      t5 = {
        label: t4,
        to: "/profile/notifications"
      };
      $[13] = t4;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== t) {
      t6 = t("notificationSettings.title");
      $[15] = t;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== t6) {
      t7 = {
        label: t6
      };
      $[17] = t6;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== t5 || $[20] !== t7) {
      t8 = [t5, t7];
      $[19] = t5;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    t2 = <ProfilePanel crumbs={t8}>{prefs.isPending ? <div className="flex flex-col gap-4">{[0, 1, 2].map(_temp5)}</div> : prefs.isError || !data ? <ErrorState onRetry={() => void prefs.refetch()} /> : <div className="flex flex-col gap-4"><Card title={t("notificationSettings.categoriesTitle")} hint={t("notificationSettings.subtitle")}><div className="grid gap-3 md:grid-cols-2">{CATEGORY_KEYS.map(key => <CheckRow key={key} checked={data.categories[key]} disabled={update.isPending} title={t(`notificationSettings.categories.${key}.label`)} hint={t(`notificationSettings.categories.${key}.hint`)} onChange={next => save({
              categories: {
                [key]: next
              }
            })} />)}</div></Card><Card title={t("notificationSettings.frequencyTitle")} hint={t("notificationSettings.frequencyHint")}><div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label={t("notificationSettings.frequencyTitle")}>{FREQUENCIES.map(freq => <ChipButton key={freq} active={data.marketingFrequency === freq} disabled={update.isPending} onClick={() => save({
              marketingFrequency: freq
            })}>{t(`notificationSettings.frequency.${freq}`)}</ChipButton>)}</div>{(data.marketingFrequency === "daily" || data.marketingFrequency === "weekly") && <p className="mt-2 text-[12px] leading-4 text-[#1F1F1F]/64">{t(`notificationSettings.frequencyDigest.${data.marketingFrequency}`)}</p>}</Card><Card title={t("notificationSettings.signalsTitle")}><div className="flex flex-col gap-3"><CheckRow checked={data.signals.digest} disabled={update.isPending || !marketingOn} title={t("notificationSettings.signals.digest.label")} hint={marketingOn ? t("notificationSettings.signals.digest.hint") : t("notificationSettings.signals.digest.off")} onChange={next_0 => save({
              signals: {
                digest: next_0
              }
            })} /><CheckRow checked={data.signals.priceDrop} disabled={update.isPending} title={t("notificationSettings.signals.priceDrop.label")} hint={t("notificationSettings.signals.priceDrop.hint")} onChange={next_1 => save({
              signals: {
                priceDrop: next_1
              }
            })} /><CheckRow checked={data.signals.backInStock} disabled={update.isPending} title={t("notificationSettings.signals.backInStock.label")} hint={t("notificationSettings.signals.backInStock.hint")} onChange={next_2 => save({
              signals: {
                backInStock: next_2
              }
            })} /></div></Card><TelegramCard /><div className="grid gap-4 lg:grid-cols-2"><Card title={t("notificationSettings.mutedTitle")} hint={t("notificationSettings.mutedHint")}><MutedList items={data.mutedShopIds.map(id => ({
              id,
              label: shopName.get(id) ?? id
            }))} empty={t("notificationSettings.mutedEmpty")} removeLabel={t("notificationSettings.unmute")} disabled={update.isPending} onRemove={id_0 => save({
              mutedShopIds: data.mutedShopIds.filter(x => x !== id_0)
            })} /><div className="mt-3"><Select value="" onValueChange={id_1 => {
                if (id_1) {
                  save({
                    mutedShopIds: [...data.mutedShopIds, id_1]
                  });
                }
              }} disabled={update.isPending}><SelectTrigger className="w-full" aria-label={t("notificationSettings.muteAdd")}><SelectValue placeholder={t("notificationSettings.muteAddPlaceholder")} /></SelectTrigger><SelectContent>{(shopsQuery.data ?? []).filter(shop_0 => !data.mutedShopIds.includes(shop_0.id)).map(_temp6)}</SelectContent></Select></div></Card><Card title={t("notificationSettings.mutedCategoriesTitle")} hint={t("notificationSettings.mutedCategoriesHint")}><MutedList items={mutedCategoryIds.map(id_2 => ({
              id: id_2,
              label: categoryName.get(id_2) ?? id_2
            }))} empty={t("notificationSettings.mutedCategoriesEmpty")} removeLabel={t("notificationSettings.unmute")} disabled={update.isPending} onRemove={id_3 => save({
              mutedCategoryIds: mutedCategoryIds.filter(x_0 => x_0 !== id_3)
            })} /><div className="mt-3"><Select value="" onValueChange={id_4 => {
                if (id_4) {
                  save({
                    mutedCategoryIds: [...mutedCategoryIds, id_4]
                  });
                }
              }} disabled={update.isPending}><SelectTrigger className="w-full" aria-label={t("notificationSettings.muteCategoryAdd")}><SelectValue placeholder={t("notificationSettings.muteCategoryPlaceholder")} /></SelectTrigger><SelectContent>{categoryOptions.filter(c_0 => !mutedCategoryIds.includes(c_0.id)).map(_temp7)}</SelectContent></Select></div></Card></div><p className="text-[12px] leading-4 text-[#1F1F1F]/64">{t("notificationSettings.topicsHint")}{" "}<Link to="/profile/notifications?settings=1" className="rounded-sm text-[#1F1F1F] underline underline-offset-2 outline-none hover:text-[#FA5C2E] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{t("notificationSettings.topicsLink")}</Link></p></div>}</ProfilePanel>;
    $[2] = categoriesQuery.data;
    $[3] = prefs;
    $[4] = shopsQuery.data;
    $[5] = t;
    $[6] = update;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function _temp7(c_1) {
  return <SelectItem key={c_1.id} value={c_1.id}>{c_1.label}</SelectItem>;
}
function _temp6(shop_1) {
  return <SelectItem key={shop_1.id} value={shop_1.id}>{shop_1.name}</SelectItem>;
}
function _temp5(i) {
  return <Skeleton key={i} className="h-32 rounded-[20px] bg-white" />;
}
function _temp4(c) {
  return [c.id, c.label];
}
function _temp3(shop) {
  return [shop.id, shop.name];
}
function _temp2() {
  return api.categories.list();
}
function _temp() {
  return api.shops.list();
}